import { useFlags } from 'launchdarkly-react-client-sdk'
import { useMemo } from 'react'
import { Field, Form } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { faCrown, faFire } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'

import { TierOfferings } from '@infura/packages.api'
import { DEVELOPER_DASHBOARD_URL, FEATURE_FLAGS } from '@infura/packages.common/constants'
import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { ActionLink } from '@infura/packages.ui/ActionLink'
import { Button } from '@infura/packages.ui/Button'
import { Modal } from '@infura/packages.ui/Modal'
import { RadioCheckbox } from '@infura/packages.ui/Radio'
import { Text } from '@infura/packages.ui/Text'
import { ServiceTier } from '@infura/packages.uks'

import { useCurrentTier } from 'hooks/useCurrentTier'
import useSubscription from 'hooks/useSubscription'
import { useUKSServicePlan } from 'hooks/useUKSServicePlan'

import { BILLING_PLAN_ROUTE } from 'lib/constants'
import { getOnCreditPlan } from 'lib/utils/creditPlan'
import { displayAsMillions, displayAsThousands } from 'lib/utils/formatting'

interface Props {
  tiers: TierOfferings
  selectedPlan: ServiceTier
  setselectedPlan: (plan: ServiceTier) => void
  isUpgradePlanModalVisible: boolean
  toggleUpgradePlanModalVisible: () => void
  toggleCheckoutModalVisible: () => void
  upgradePlanReason?: string
  useUpgradePlanModalVariation?: boolean
}

export const UpgradePlanModal = ({
  isUpgradePlanModalVisible,
  selectedPlan,
  setselectedPlan,
  tiers,
  toggleCheckoutModalVisible,
  toggleUpgradePlanModalVisible,
  upgradePlanReason,
  useUpgradePlanModalVariation = true
}: Props) => {
  const { servicePlan } = useUKSServicePlan()
  const flags = useFlags()
  const chargify = useSubscription()
  const apiKeyPolicyVariation = useMemo(() => flags[FEATURE_FLAGS.API_KEY_POLICY_VARIATION], [flags])
  const currentTier = useCurrentTier(chargify.subscription, servicePlan)
  const isOnCreditPlan = getOnCreditPlan(servicePlan)

  const handleSubmit = values => {
    setselectedPlan(values.tier)
  }
  const currentTierIndex = Object.keys(tiers).findIndex(tier => tier === currentTier)
  const filteredTiers = Object.entries(tiers).filter(
    (tier, index) => tier[0] !== ServiceTier.ENTERPRISE && index > currentTierIndex
  )

  const formState: { tier: ServiceTier } = {
    tier:
      selectedPlan ||
      (filteredTiers && !!filteredTiers.length && (filteredTiers[0][0] as ServiceTier)) ||
      ServiceTier.CORE
  }

  return (
    <Modal
      heading={
        <div className='flex items-center'>
          <span className='mb-0.5 mr-2 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-yellow-600'>
            <FontAwesomeIcon className='text-[10px] text-white' icon={faCrown} />
          </span>
          <span className='text-text-alternative text-xs font-normal'>Upgrade to use this feature</span>
        </div>
      }
      open={isUpgradePlanModalVisible}
      setIsOpen={toggleUpgradePlanModalVisible}
    >
      <Form
        initialValues={formState}
        keepDirtyOnReinitialize
        onSubmit={handleSubmit}
        render={({ handleSubmit, values }) => {
          return (
            <form className='grid grid-cols-1 gap-y-4' onSubmit={handleSubmit}>
              <div>
                <div className='font-xl text-text-default mb-2 text-center font-bold' data-testid='upgrade_modal_title'>
                  Ready to level up?
                </div>
                <div className='text-text-default pt-2' data-testid='upgrade_modal_content'>
                  {upgradePlanReason ? (
                    upgradePlanReason
                  ) : (
                    <>
                      You’ve reached the maximum number of API keys for your current plan. Upgrade now to unlock the
                      full potential of Infura.
                    </>
                  )}
                </div>
              </div>
              <span>
                {!useUpgradePlanModalVariation || apiKeyPolicyVariation === 'A' ? (
                  <>
                    <div className='grid grid-cols-1 gap-y-4'>
                      {filteredTiers.map(([tier, offering]) => {
                        const isCurrentTier = values.tier === tier
                        const isDeveloperPlan = offering.name === 'Developer'
                        return (
                          <Field key={tier} name='tier'>
                            {({ input }) => {
                              return (
                                <div
                                  className={clsx(
                                    'border-border-default group cursor-pointer grid-cols-1 overflow-hidden rounded-md border transition-[height] duration-300 ease-linear',
                                    isCurrentTier ? 'delay-50' : 'h-[42px]',

                                    isOnCreditPlan
                                      ? isCurrentTier && isDeveloperPlan
                                        ? 'h-[182px]'
                                        : 'h-[148px]'
                                      : isCurrentTier && isDeveloperPlan
                                      ? 'h-[148px]'
                                      : 'h-[114px]'
                                  )}
                                  onClick={() => {
                                    input.onChange(tier)
                                  }}
                                >
                                  {isCurrentTier && isDeveloperPlan ? (
                                    <div className='bg-background-alternative text-text-alternative rounded-t p-2 text-center text-xs'>
                                      <FontAwesomeIcon className={clsx('mr-2 mt-0.5', {})} icon={faFire} size='sm' />
                                      Most Popular
                                    </div>
                                  ) : null}
                                  <div className='px-4 py-2' data-testid='upgrade_modal_tier'>
                                    <div className='flex justify-between'>
                                      <div className='flex items-center'>
                                        <RadioCheckbox
                                          checked={isCurrentTier}
                                          className={clsx({
                                            'group-hover:border-background-default group-hover:bg-primary-default group-hover:ring-primary-default group-hover:ring-2':
                                              !isCurrentTier
                                          })}
                                        />
                                        <label
                                          className='font-body ml-2 cursor-pointer text-base normal-case'
                                          htmlFor={`tier_${tier}`}
                                        >
                                          <Text as='span' data-testid='upgrade_modal_tier_name' theme='bold'>
                                            {offering.name}
                                          </Text>
                                        </label>
                                      </div>
                                      <Text
                                        as='span'
                                        className='mb-0 ml-2 text-gray-700'
                                        data-testid='upgrade_modal_tier_price'
                                        theme='bold'
                                      >
                                        {`US$${offering.price} /month`}
                                      </Text>
                                    </div>
                                    {isCurrentTier ? (
                                      <ul className='pt-4'>
                                        <li className='mb-2'>
                                          <FontAwesomeIcon className='text-text-default mr-2' icon={faCheck} />
                                          <Text as='span' data-testid='upgrade_modal_tier_key_limit'>
                                            <span>{isDeveloperPlan ? '5 ' : 'Unlimited '}</span>
                                            <span>API Keys</span>
                                          </Text>
                                        </li>
                                        {isOnCreditPlan ? (
                                          <>
                                            <li className='mb-2'>
                                              <FontAwesomeIcon className='text-text-default mr-2' icon={faCheck} />
                                              <Text as='span' data-testid='upgrade_modal_tier_key_credits_per_day'>
                                                <span>{displayAsMillions(offering.creditsPerDay)}</span>
                                                <span> credits per day</span>
                                              </Text>
                                            </li>
                                            <li className=''>
                                              <FontAwesomeIcon className='text-text-default mr-2' icon={faCheck} />
                                              <Text as='span' data-testid='upgrade_modal_tier_key_credits_per_second'>
                                                <span>{displayAsThousands(offering.creditsPerSecond)}</span>
                                                <span> credits per second</span>
                                              </Text>
                                            </li>
                                          </>
                                        ) : (
                                          <li className=''>
                                            <FontAwesomeIcon className='text-text-default mr-2' icon={faCheck} />
                                            <Text as='span' data-testid='upgrade_modal_tier_key_requests'>
                                              {offering.requests}
                                            </Text>
                                          </li>
                                        )}
                                      </ul>
                                    ) : null}
                                  </div>
                                </div>
                              )
                            }}
                          </Field>
                        )
                      })}
                    </div>
                    <div className='mt-2 flex items-center gap-2'>
                      <p className='mb-0'>Not sure which plan is best for you?</p>{' '}
                      <ActionLink href={BILLING_PLAN_ROUTE}>Compare Plans</ActionLink>
                    </div>
                  </>
                ) : null}
              </span>
              <div className='flex flex-col-reverse sm:flex-row'>
                <Button
                  className='mt-4 sm:mt-0'
                  onClick={() => {
                    toggleUpgradePlanModalVisible()
                    trackClickForSegmentAnalytics({
                      clickText: 'Cancel',
                      clickUrl: `${DEVELOPER_DASHBOARD_URL}`,
                      location: 'Dashboard - Upgrade Plan Modal',
                      type: 'Text'
                    })
                  }}
                  theme='secondary'
                >
                  Cancel
                </Button>
                {!useUpgradePlanModalVariation || apiKeyPolicyVariation === 'A' ? (
                  <Button
                    className='sm:ml-4'
                    data-testid='upgrade-modal-proceed-to-checkout'
                    id='upgrade-modal-proceed-to-checkout'
                    onClick={() => {
                      toggleUpgradePlanModalVisible()
                      trackClickForSegmentAnalytics({
                        clickText: 'Proceed to Checkout',
                        clickUrl: `${DEVELOPER_DASHBOARD_URL}`,
                        location: 'Dashboard - Upgrade Plan Modal',
                        type: 'Text'
                      })
                      toggleCheckoutModalVisible()
                    }}
                    type='submit'
                  >
                    Proceed to Checkout
                  </Button>
                ) : (
                  <Button
                    className='flex items-center sm:ml-4'
                    data-testid='upgrade-modal-upgrade'
                    href={BILLING_PLAN_ROUTE}
                    id='upgrade-modal-upgrade'
                    onClick={() => {
                      trackClickForSegmentAnalytics({
                        clickText: 'Upgrade',
                        clickUrl: BILLING_PLAN_ROUTE,
                        location: 'Dashboard - Upgrade Plan Modal',
                        type: 'Text'
                      })
                    }}
                  >
                    <FontAwesomeIcon className='mb-0.5 mr-2 p-[3px] text-[10px]' icon={faCrown} />
                    <span>Upgrade</span>
                  </Button>
                )}
              </div>
            </form>
          )
        }}
      />
    </Modal>
  )
}
